import { useTranslation } from "react-i18next";
import { validateExactMatch, RecoveryPhraseType } from "@/schemas/recoveryKey";

import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import { encryptPasswordWithRecoveryKey } from "@/middleware/recoveryKey";
import { toast } from "@/hooks/useToast";
import { PHRASE_WORDS_COUNT } from "@/config/constants";
import BaseConfirmPhraseStep from "../Common/BaseConfirmPhraseStep";

export default function ConfirmPhraseEncryptStep({
  onPrevious,
  onClose,
  formData,
}: FormStepProps<RecoveryPhraseType>) {
  const { t } = useTranslation();
  // `formData` is ALWAYS supplied by Wizard (it starts life as `{}`), so an
  // optional chain that stops at `formData` guards nothing — the words key is
  // what may be missing, e.g. when this step is reached before WordsStep has
  // finished generating. Chain past the cast so an absent phrase degrades to
  // `[]` (which can never match what the user types) instead of throwing
  // during render.
  const originalPhrase =
    (formData?.words as { words?: string[] } | undefined)?.words ?? [];
  const { password } = (formData?.password ?? {}) as { password?: string };

  async function onSubmit(recoveryPhrase: RecoveryPhraseType) {
    const enteredWords = recoveryPhrase.phrase
      .map((word) => word.toLowerCase())
      .join(" ");
    // Same shape as the words cast above. This one sits inside the try, so it
    // surfaced as a toast rather than a crash — but the toast printed the raw
    // `Cannot read properties of undefined` text. Fail with the normal error
    // copy instead, and never hand `undefined` to the encryption call.
    if (!password) {
      toast({
        description: t("errorOccurred"),
        variant: "destructive",
      });
      return;
    }
    try {
      await encryptPasswordWithRecoveryKey(enteredWords, password);
      onClose?.();
      toast({
        description: t("recover.confirmPhraseSuccess"),
      });
    } catch (error) {
      toast({
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }

  return (
    <BaseConfirmPhraseStep<string[]>
      title={t("recover.wordsContainerTitle")}
      description={t("recover.confirmPhraseDescription", {
        wordsCount: PHRASE_WORDS_COUNT,
      })}
      onSubmit={onSubmit}
      onPrevious={onPrevious}
      onClose={onClose}
      validator={{
        validate: validateExactMatch,
        validationParams: originalPhrase,
      }}
    />
  );
}
